import { Box, Typography, Tooltip, styled } from '@mui/material'
import { LOANPRO_BASE_URL, TENANT_ID, LOAN_RECORD_URL_PATH } from '@/utils/constants'
import { formatDate, replaceNullValues, formatCents } from '@/utils/common'
import { ILoanDetail } from '@/types'

interface LoanSummaryProps {
  loan: ILoanDetail
}

export const LoanSummary = ({ loan }: LoanSummaryProps) => {
  const safeloan = replaceNullValues<ILoanDetail>(loan)

  const loanProUrl = `${LOANPRO_BASE_URL}#/t_/${TENANT_ID}${LOAN_RECORD_URL_PATH}?loanid=${safeloan.providerId}&view=summary`

  return (
    <Box
      sx={{
        borderBottom: 'none',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <ResponsiveWrapper>
        {safeloan.providerId != 0 && (
          <DetailBox>
            <Label>Provider Id</Label>
            <a href={loanProUrl} target="_blank" rel="noopener noreferrer">
              {safeloan.providerId}
            </a>
          </DetailBox>
        )}

        <DetailBox>
          <Label>Product Id</Label>
          <Value
            sx={{
              maxWidth: 160,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <Tooltip title={safeloan.productId} placement="bottom" arrow>
              <span>{safeloan.productId}</span>
            </Tooltip>
          </Value>
        </DetailBox>

        <DetailBox>
          <Label>Product Key</Label>
          <Value
             sx={{
               maxWidth: 160,
               whiteSpace: 'nowrap',
               overflow: 'hidden',
               textOverflow: 'ellipsis',
             }}
           >
             <Tooltip title={safeloan.productKey ?? ''} placement="bottom" arrow>
               <span>{safeloan.productKey}</span>
             </Tooltip>
           </Value>
        </DetailBox>

        <DetailBox>
          <Label>Application Id</Label>
          <Value>{safeloan.loanApplicationId}</Value>
        </DetailBox>

        <DetailBox>
          <Label>Fee Amount</Label>
          <Value>{formatCents(safeloan.fee)}</Value>
        </DetailBox>

        <DetailBox>
          <Label>Interest Amount</Label>
          <Value>{formatCents(safeloan.interest)}</Value>
        </DetailBox>

        <DetailBox>
          <Label>Write-off date</Label>
          <Value>{safeloan.writtenOffDate}</Value>
        </DetailBox>

        <DetailBox>
          <Label>Created At</Label>
          <Value>{formatDate(safeloan.createdAt)}</Value>
        </DetailBox>
      </ResponsiveWrapper>
    </Box>
  )
}

const ResponsiveWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  padding: `${theme.spacing(1)} 0 0 0`,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}))

const DetailBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px',
  border: '1px solid #E4E7EC',
  height: '40px',
  width: '33.3%',

  [theme.breakpoints.down('md')]: {
    width: '50%',
  },

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: '60px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
  },
}))

const Label = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: '#344054',
  [theme.breakpoints.down('md')]: {
    fontSize: '13px',
  },
}))

const Value = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 400,
  color: '#475467',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    wordBreak: 'break-word',
  },
}))
